import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ILabSubmission } from '../../models/account/lab-submission';
import { IRegistrationSubmission } from '../../models/account/registration-submission';
import { AccountService } from '../../services/account/account.service';
import { SettingsService } from 'app/services/core/settings.service';
import { UserService } from 'app/services/user/user.service';

@Component({
  selector: 'register-lab',
  templateUrl: './register-lab.component.html',
  styleUrls: ['./register-lab.component.scss']
})
export class RegisterLabComponent implements OnInit {
  labs: ILabSubmission[] = [];
  selectedLabs: ILabSubmission[] = [];
  states: string[] = [];
  submissionSuccessful: boolean = true;
  submissionComplete: boolean = false;

  labForm = new UntypedFormGroup({
    orelapId: new UntypedFormControl({value: null, disabled: true}),
    laboratoryName: new UntypedFormControl('', Validators.required),
    accreditationType: new UntypedFormControl('Primary'),
    primaryAa: new UntypedFormControl({ value: 'OR', disabled: true }, [Validators.required]),
    secondaryAa: new UntypedFormControl(''),
    siteAddress: new UntypedFormControl(null),
    siteCity: new UntypedFormControl(null),
    siteState: new UntypedFormControl(null),
    siteZipCode: new UntypedFormControl(null, Validators.pattern(/^\d{5}(-\d{4})?$/)),
    sitePhone: new UntypedFormControl(null, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)),
    siteFax: new UntypedFormControl(null, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)),
    siteEmail: new UntypedFormControl(null, Validators.email),
    mobile: new UntypedFormControl('N'),
    vin: new UntypedFormControl(null),
    licensePlate: new UntypedFormControl(null),
    licenseState: new UntypedFormControl(null),
    approvedByAdmin: new UntypedFormControl('N'),
    epaId: new UntypedFormControl(null),
    registrationId: new UntypedFormControl(0),
    status: new UntypedFormControl('Pending'),
  });

  displayedColumns: string[] = ['orelapId', 'tniId', 'laboratoryName'];
  dataSource = new MatTableDataSource<ILabSubmission>(this.labs);
  @ViewChild('TableSort') sort: MatSort;
  @ViewChild('TablePaginator') paginator: MatPaginator;

  selectedColumns: string[] = ['laboratoryName', 'delete'];
  selectedSource = new MatTableDataSource<ILabSubmission>(this.selectedLabs);

  constructor(public settings: SettingsService, 
    private accountService: AccountService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.states = this.accountService.states;

    this.userService.getAppUser().subscribe(
      response => this.accountService.loginName = response.loginName,
      error => console.log('error', error),
      () => {}
    );

    this.accountService.getRegistrationLabs().subscribe(
      response => this.labs = response,
      error => console.log('error', error),
      () => {
        this.dataSource = new MatTableDataSource<ILabSubmission>(this.labs);
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
      }
    );
  }

  filterTable(value: string) {
    this.dataSource.filter = value.trim().toLowerCase();
  }

  selectLab(lab: ILabSubmission): void {
    this.labForm.patchValue({
      orelapId: lab.orelapId,
      laboratoryName: lab.laboratoryName,
      accreditationType: lab.accreditationType,
      primaryAa: lab.primaryAa,
      secondaryAa: '',
      siteAddress: lab.siteAddress,
      siteCity: lab.siteCity,
      siteState: lab.siteState,
      siteZipCode: lab.siteZipCode,
      sitePhone: lab.sitePhone,
      siteFax: lab.siteFax,
      siteEmail: lab.siteEmail,
      mobile: lab.mobile,
      vin: lab.vin,
      licensePlate: lab.licensePlate,
      licenseState: lab.licenseState,
      approvedByAdmin: lab.approvedByAdmin,
      epaId: lab.epaId,
      registrationId: lab.registrationId,
      status: lab.status
    });
    this.labForm.get('laboratoryName').disable();
    this.labForm.get('accreditationType').disable();
    this.labForm.get('siteAddress').disable();
    this.labForm.get('siteCity').disable();
    this.labForm.get('siteState').disable();
    this.labForm.get('siteZipCode').disable();
    this.labForm.get('sitePhone').disable();
    this.labForm.get('siteFax').disable();
    this.labForm.get('siteEmail').disable();
    this.labForm.get('mobile').disable();
    this.labForm.get('vin').disable();
    this.labForm.get('licensePlate').disable();
    this.labForm.get('licenseState').disable();
  }

  addLab(): void {
    this.labForm.get('laboratoryName').setValidators([Validators.required]);
    this.labForm.get('laboratoryName').updateValueAndValidity();
    if (this.labForm.valid && this.labForm.get('primaryAa').value !== null) {
      let form = this.labForm.getRawValue();
      let newLab: ILabSubmission = {
        accreditationType: form.accreditationType,
        primaryAa: form.primaryAa,
        siteAddress: form.siteAddress,
        laboratoryName: form.laboratoryName,
        licensePlate: form.licensePlate,
        licenseState: form.licenseState,
        mobile: form.mobile,
        orelapId: form.orelapId,
        siteCity: form.siteCity,
        siteEmail: form.siteEmail,
        siteFax: form.siteFax,
        sitePhone: form.sitePhone,
        siteState: form.siteState,
        siteZipCode: form.siteZipCode,
        tniId: '',
        vin: form.vin,
        approvedByAdmin: form.approvedByAdmin,
        epaId: form.epaId,
        registrationId: form.registrationId,
        status: form.status
      };
      this.selectedLabs.push(newLab);
      this.selectedSource = new MatTableDataSource<ILabSubmission>(this.selectedLabs);
      this.clearLab();
    }
    else {
      this.labForm.get('laboratoryName').markAsTouched();
      alert("Please complete required fields");
    }
  }

  removeLab(labName: string): void {
    this.selectedLabs = this.selectedLabs.filter(lab => lab.laboratoryName != labName);
    this.selectedSource = new MatTableDataSource<ILabSubmission>(this.selectedLabs);
  }

  clearLab(): void {
    this.labForm.patchValue({
      orelapId: null,
      laboratoryName: null,
      accreditationType: 'Primary',
      primaryAa: 'OR',
      secondaryAa: null,
      siteAddress: null,
      siteCity: null,
      siteState: null,
      siteZipCode: null,
      sitePhone: null,
      siteFax: null,
      siteEmail: null,
      mobile: 'N',
      vin: null,
      licensePlate: null,
      licenseState: null,
      approvedByAdmin: 'N',
      epaId: null,
      registrationId: 0,
      status: 'Pending'
    });
    this.labForm.get('laboratoryName').enable();
    this.labForm.get('accreditationType').enable();
    this.labForm.get('siteAddress').enable();
    this.labForm.get('siteCity').enable();
    this.labForm.get('siteState').enable();
    this.labForm.get('siteZipCode').enable();
    this.labForm.get('sitePhone').enable();
    this.labForm.get('siteFax').enable();
    this.labForm.get('siteEmail').enable();
    this.labForm.get('mobile').enable();
    this.labForm.get('vin').enable();
    this.labForm.get('licensePlate').enable();
    this.labForm.get('licenseState').enable();
    this.labForm.get('laboratoryName').clearValidators();
    this.labForm.get('laboratoryName').updateValueAndValidity();
  }

  addSecondaryAa(): void {
    let primaryAa = this.labForm.get('primaryAa').value;
    let secondaryAa = this.labForm.get('secondaryAa').value;
    this.labForm.patchValue({
      primaryAa: primaryAa === null ? secondaryAa :  primaryAa + ';' + secondaryAa
    });
  }

  clearPrimaryAa(): void {
    this.labForm.patchValue({
      primaryAa: null
    });
  }

  submitLabRegistration(): void {
    let submission: IRegistrationSubmission = {
      labs: this.selectedLabs,
      loginName: this.accountService.loginName
    }

    this.accountService.registerLabs(submission).subscribe(
      response => {
        this.submissionSuccessful = response;
        this.submissionComplete = true;
      },
      error => console.log('error', error),
      () => {
        this.submissionComplete = true;
        this.selectedLabs = [];
        this.selectedSource = new MatTableDataSource<ILabSubmission>(this.selectedLabs);
      }
    );
  }

}
